/* eslint-disable */
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import axios from "axios";
import { Button, Collapse, Form, FormInstance, Input, Typography, message, Modal, Switch } from "antd";
import { useValidators } from "engine/hooks";
import { languages, translationStatuses } from "helpers/constants";
import { defaultRequest } from "../../../helpers";
import { IUser } from "engine/store/auth/types";
import { getAvailableLanguages, canEditLanguage, isAdmin } from "helpers/permissions";
import { TranslationsHistory } from "../translations-history";
import HistoryOutlined from "@ant-design/icons/HistoryOutlined";

interface ITranslationForm {
  form: FormInstance<any>;
  articleForm?: FormInstance<any>;
  type: "tag" | "article";
  linkLanguage?: string;
  user?: IUser | null;
  enableAutoTranslate?: boolean;
  onFinish?(data: any): void;
  onFieldsChange?(changedValues: any[]): void;
}

export const TranslationsForm = ({
  form,
  articleForm,
  type,
  onFinish,
  onFieldsChange,
  linkLanguage,
  user,
  enableAutoTranslate = true,
}: ITranslationForm) => {
  const { t } = useTranslation();
  const { required } = useValidators();
  
  // Local state for the switch
  const [autoTranslateEnabled, setAutoTranslateEnabled] = useState(false);
  
  // State to track history visibility per language
  const [historyVisible, setHistoryVisible] = useState<Record<string, boolean>>({});

  // Get available languages for the current user
  const availableLanguageCodes = getAvailableLanguages(user || null);
  const filteredLanguages = languages.filter((lang) => availableLanguageCodes.includes(lang.name));
  
  // Sync local state with form value on mount and when form changes
  useEffect(() => {
    if (articleForm) {
      const formValue = articleForm.getFieldValue("enableAutoTranslate");
      setAutoTranslateEnabled(formValue || false);
    }
  }, [articleForm, enableAutoTranslate]);

  const handleTranslate = async (lang: string) => {
    try {
      const title = form.getFieldValue(["en", "title"]);
      const body = form.getFieldValue(["en", "body"]);

      if (!title) {
        message.error("Title is required for translation.");
        return;
      }

      const textToTranslate = type === "tag" ? [title] : [title, body].filter(Boolean);

      let response = await defaultRequest.post(`articles/translate`, {
        source: "en",
        target: lang,
        q: textToTranslate,
        format: "text",
      });

      const translations = response?.data?.translations || [];
      if (!translations.length) throw new Error("No translation returned from Google API.");

      form.setFieldValue([lang, "title"], translations[0]?.translatedText || "");
      if (type !== "tag" && translations[1]) {
        form.setFieldValue([lang, "body"], translations[1]?.translatedText || "");
      }

      form.setFieldValue([lang, "status"], "auto-generated");

      message.success("Translation created successfully.");
    } catch (error) {
      message.error("Error while translating with Google.");
    }
  };

  const showTranslationConfirm = (lang: string) => {
    Modal.confirm({
      title: "Are you sure you want to auto-generate the texts with Google Translate?",
      content: "Info: We limit the budget of the translation API at 10.- CHF a month.",
      okText: "Yes, proceed",
      cancelText: "Cancel",
      onOk: () => handleTranslate(lang),
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      className="translation-form"
      onFinish={onFinish}
      onFieldsChange={onFieldsChange}
    >
      {/* Enable Auto-Translate switch - Admin only, Article only */}
      {type === "article" && isAdmin(user || null) && articleForm && (
        <div style={{ marginBottom: "1.5rem", display: "flex", alignItems: "center", gap: "12px" }}>
          <label style={{ marginBottom: 0 }}>Enable Auto-Translate for translators:</label>
          <Switch
            checked={autoTranslateEnabled}
            onChange={(checked) => {
              setAutoTranslateEnabled(checked);
              articleForm.setFieldValue("enableAutoTranslate", checked);
            }}
            title="Allow this article to be translated automatically using Google Translate"
          />
        </div>
      )}
      
      <Collapse expandIconPosition="end" defaultActiveKey={["en"]}>
        {filteredLanguages.map((language) => (
          <Collapse.Panel
            forceRender
            key={language.name}
            header={
              <div className="panel-header">
                <Typography>{language.title}</Typography>
                {language.name !== "en" && (
                  <Form.Item shouldUpdate noStyle>
                    {({ getFieldValue }) => (
                      <span
                        className="status"
                        style={{
                          backgroundColor: translationStatuses.find(
                            (status) => status.name === getFieldValue([language.name, "status"]),
                          )?.color,
                          padding: "0.2rem 0.5rem",
                          borderRadius: "0.375rem",
                        }}
                      >
                        {t(
                          `common.translationStatuses.${
                            getFieldValue([language.name, "status"]) ?? "not-translated"
                          }`,
                        )}
                      </span>
                    )}
                  </Form.Item>
                )}
              </div>
            }
          >
            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue }) => (
                <>
                  <Form.Item noStyle name={[language.name, "status"]}>
                    <Input type="hidden" />
                  </Form.Item>
                  <Form.Item
                    label={t("common.title")}
                    name={[language.name, "title"]}
                    rules={language.name === "en" ? [required] : []}
                  >
                    <Input
                      disabled={
                        !canEditLanguage(user || null, language.name) ||
                        (type !== "tag" &&
                          language.name !== "en" &&
                          !getFieldValue(["en", "title"]))
                      }
                    />
                  </Form.Item>
                  {type === "article" && (
                    <Form.Item
                      label={t("articles.articleDescription")}
                      name={[language.name, "body"]}
                      rules={language.name === "en" ? [required] : []}
                    >
                      <Input.TextArea
                        rows={6}
                        disabled={
                          !canEditLanguage(user || null, language.name) ||
                          (language.name !== "en" && !getFieldValue(["en", "body"]))
                        }
                      />
                    </Form.Item>
                  )}

                  {type === "article" && linkLanguage !== language.name && (
                    <Form.Item
                      label={t("articles.translatedLink")}
                      name={[language.name, "translatedLink"]}
                    >
                      <Input />
                    </Form.Item>
                  )}
                </>
              )}
            </Form.Item>

            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue }) =>
                ((type === "tag" && getFieldValue(["en", "title"])) ||
                  (type === "article" &&
                    getFieldValue(["en", "title"]) &&
                    getFieldValue(["en", "body"]))) && (
                  <div className="manual-translation--action-btns">
                    {language.name !== "en" && user?.role !== "author" && (
                      <Button 
                        type="primary" 
                        onClick={() => showTranslationConfirm(language.name)}
                        disabled={!autoTranslateEnabled && !isAdmin(user || null)}
                        title={!autoTranslateEnabled && !isAdmin(user || null) ? "Auto-translate is disabled for this article. Contact an admin to enable it." : ""}
                      >
                        {t("common.autogenerateByGoogleTranslate")}
                      </Button>
                    )}

                    {language.name !== "en" &&
                      ((type === "tag" && getFieldValue([language.name, "title"])) ||
                        (type === "article" &&
                          getFieldValue([language.name, "title"]) &&
                          getFieldValue([language.name, "body"]))) && (
                        <Button
                          type="link"
                          danger
                          onClick={() => {
                            form.resetFields([[language.name, "title"]]);
                            form.setFieldValue([language.name, "status"], "not-translated");
                            if (type === "article") form.resetFields([[language.name, "body"]]);
                          }}
                        >
                          {t("common.deleteThisTranslation")}
                        </Button>
                      )}
                  </div>
                )
              }
            </Form.Item>

            {/* History Toggle Button and Component */}
            {type === "article" && (
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => {
                  const currentTranslation = {
                    title: getFieldValue([language.name, "title"]),
                    body: getFieldValue([language.name, "body"]),
                    status: getFieldValue([language.name, "status"]),
                    translatedLink: getFieldValue([language.name, "translatedLink"]),
                    history: getFieldValue([language.name, "history"]) || [],
                  };
                  const hasHistory = currentTranslation.history && currentTranslation.history.length > 0;


                  return (
                    <>
                      {hasHistory && (
                        <div style={{ marginTop: "1rem" }}>
                          <Button
                            type="default"
                            onClick={() => {
                              setHistoryVisible((prev) => ({
                                ...prev,
                                [language.name]: !prev[language.name],
                              }));
                            }}
                            icon={<HistoryOutlined />}
                          >
                            {historyVisible[language.name] ? "Close History" : "See History"}
                          </Button>
                        </div>
                      )}
                      
                      {hasHistory && (
                        <TranslationsHistory
                          currentTranslation={currentTranslation as any}
                          language={language.name}
                          visible={historyVisible[language.name] || false}
                        />
                      )}
                    </>
                  );
                }}
              </Form.Item>
            )}
          </Collapse.Panel>
        ))}
      </Collapse>
    </Form>
  );
};
